import { Injectable } from "@angular/core";
import { ClientType } from "./com/comEnvironmentClient";
import { CoreCOMService } from "./com.service";

@Injectable({
  providedIn: "root"
})
export class ViewportHelperService {

  // Viewport and keyboard configuration: defines thresholds and delays for mobile viewport adjustments
  private readonly VIEWPORT_CONFIG = {
    MIN_VH_PX: 400,
    KEYBOARD_HEIGHT_THRESHOLD: 140,
    WIDTH_CHANGE_THRESHOLD: 12,
    KEYBOARD_CLOSE_DELAY: 300,
    INPUT_ELEMENT_REGEX: /^(INPUT|TEXTAREA)$/
  };

  // CSS custom property names used for dynamic viewport calculations
  private readonly CSS_CUSTOM_PROPERTIES = {
    VH: '--vh',                  // Custom viewport height unit: 1% of actual viewport height in pixels (fixes mobile browser inconsistencies)
    LEFT_PERCENT: '--lpercent',  // Left boundary percentage for asymmetric sidebar centering
    RIGHT_PERCENT: '--rpercent'  // Right boundary percentage for asymmetric sidebar centering
  };

  // Viewport dimensions: tracks last known window dimensions to detect and classify resize events
  private lastWindowHeight: number = 0;
  private lastWindowWidth: number = 0;
  public isMobileContext: boolean = false;

  // Keyboard event handling: manages delayed recalculation when virtual keyboard closes
  private keyboardCloseTimeout: number | null = null;

  // Event listeners: stored references enable proper cleanup to prevent memory leaks
  private focusoutListener: ((e: FocusEvent) => void) | null = null;
  private focusinListener: ((e: FocusEvent) => void) | null = null;

  constructor(private _coreCOMService: CoreCOMService) {
    // Cache mobile context to avoid repeated expensive checks
    this.isMobileContext = this._coreCOMService.environment.client.isMobile ||
      this._coreCOMService.environment.client.isBrowserMobileSafari;
  }

  /** Registers focusin/focusout listeners for Android mobile keyboard detection.
   *  iOS is excluded because it uses IOSHelperService.setWebviewAfterBlur() instead. */
  public registerKeyboardListeners(): void {
    const isIOS = this._coreCOMService.environment.client.type === ClientType.ios;
    if (this.isMobileContext && !isIOS) {
      this.focusoutListener = (e: FocusEvent) => this.handleInputFocusOut(e);
      this.focusinListener = (e: FocusEvent) => this.handleInputFocusIn(e);

      // Passive events improve scroll performance, capture phase ensures early detection
      document.addEventListener('focusout', this.focusoutListener, { capture: true, passive: true });
      document.addEventListener('focusin', this.focusinListener, { capture: true, passive: true });
    }
  }

  /** Removes registered keyboard listeners to prevent memory leaks. Call in ngOnDestroy. */
  public unregisterKeyboardListeners(): void {
    if (this.focusoutListener) {
      document.removeEventListener('focusout', this.focusoutListener, true);
      this.focusoutListener = null;
    }
    if (this.focusinListener) {
      document.removeEventListener('focusin', this.focusinListener, true);
      this.focusinListener = null;
    }
    if (this.keyboardCloseTimeout !== null) {
      clearTimeout(this.keyboardCloseTimeout);
      this.keyboardCloseTimeout = null;
    }
  }

  /** Sets --vh custom property and updates sidebar centering. Fixes mobile browser viewport issues. */
  public setViewHeight(): void {
    const currentHeight = window.innerHeight;
    const currentWidth = window.innerWidth;

    if (!this.isValidDimension(currentHeight) || !this.isValidDimension(currentWidth)) {
      return;
    }

    // Get safe area insets for consistent keyboard detection
    const safeAreaInsetBottom = parseInt(
      getComputedStyle(document.body).getPropertyValue("--safe-area-inset-bottom")
    ) || 0;
    const safeAreaInsetTop = parseInt(
      getComputedStyle(document.body).getPropertyValue("--safe-area-inset-top")
    ) || 0;

    const adjustedHeight = currentHeight - safeAreaInsetBottom - safeAreaInsetTop;

    // Skip layout update when mobile keyboard is open
    if (this.isKeyboardOpen(adjustedHeight, currentWidth)) {
      return;
    }

    this.lastWindowHeight = adjustedHeight;
    this.lastWindowWidth = currentWidth;

    this.updateViewportHeight(adjustedHeight);
    this.updateSidebarCentering();
  }

  /** Scrolls the currently focused element into view */
  public scrollActiveElementIntoView(): void {
    const activeElement = document.activeElement;
    if (activeElement && activeElement.scrollIntoView) {
      activeElement.scrollIntoView();
    }
  }

  /** Validates dimension is finite and positive */
  private isValidDimension(value: number): boolean {
    return Number.isFinite(value) && value > 0;
  }

  /** Detects mobile keyboard open via height decrease without width change */
  private isKeyboardOpen(currentHeight: number, currentWidth: number): boolean {
    if (!this.isMobileContext || this.lastWindowHeight === 0) {
      return false;
    }

    const heightDecrease = this.lastWindowHeight - currentHeight;
    const widthDiffAbs = Math.abs(currentWidth - this.lastWindowWidth);

    return heightDecrease > this.VIEWPORT_CONFIG.KEYBOARD_HEIGHT_THRESHOLD &&
      widthDiffAbs < this.VIEWPORT_CONFIG.WIDTH_CHANGE_THRESHOLD;
  }

  /** Updates --vh CSS custom property with minimum height protection */
  private updateViewportHeight(adjustedHeight: number): void {
    const effectiveHeight = Math.max(adjustedHeight, this.VIEWPORT_CONFIG.MIN_VH_PX);
    const vhInPx = (effectiveHeight * 0.01).toFixed(3);

    if (document.documentElement) {
      document.documentElement.style.setProperty(this.CSS_CUSTOM_PROPERTIES.VH, `${vhInPx}px`);
    }
  }

  /** Updates left/right percentage for asymmetric sidebar centering */
  private updateSidebarCentering(): void {
    const mainContent = document.getElementById("maincontent");
    const routeMainLayer = document.querySelector('.routeMainLayer') as HTMLElement | null;

    // Guard: required DOM elements must exist
    if (!mainContent || !routeMainLayer) {
      return;
    }

    const root = document.documentElement;
    if (!root) {
      return;
    }

    // Calculate asymmetric distribution when content is narrower than container
    if (mainContent.clientWidth < routeMainLayer.clientWidth) {
      const widthDiff = routeMainLayer.clientWidth - mainContent.clientWidth;
      const diffRatio = widthDiff / routeMainLayer.clientWidth;
      const percent = Math.round(diffRatio * 100);
      root.style.setProperty(this.CSS_CUSTOM_PROPERTIES.LEFT_PERCENT, `${percent}%`);
      root.style.setProperty(this.CSS_CUSTOM_PROPERTIES.RIGHT_PERCENT, `${100 - percent}%`);
    } else {
      // Reset to full width when no sidebar offset needed
      root.style.setProperty(this.CSS_CUSTOM_PROPERTIES.LEFT_PERCENT, "0%");
      root.style.setProperty(this.CSS_CUSTOM_PROPERTIES.RIGHT_PERCENT, "100%");
    }
  }

  /** Handles input focus out: triggers delayed viewport recalculation after keyboard closes */
  private handleInputFocusOut(e: FocusEvent): void {
    const target = e.target as HTMLElement;

    if (!this.isInputElement(target)) {
      return;
    }

    if (this.keyboardCloseTimeout !== null) {
      clearTimeout(this.keyboardCloseTimeout);
    }

    this.keyboardCloseTimeout = window.setTimeout(() => {
      // Reset forces keyboard detection to recalculate on next resize
      this.lastWindowHeight = 0;
      this.setViewHeight();
      this.keyboardCloseTimeout = null;
    }, this.VIEWPORT_CONFIG.KEYBOARD_CLOSE_DELAY);
  }

  /** Handles input focus in to cancel delayed viewport recalculation when switching inputs */
  private handleInputFocusIn(e: FocusEvent): void {
    const target = e.target as HTMLElement;

    if (!this.isInputElement(target)) {
      return;
    }

    if (this.keyboardCloseTimeout !== null) {
      clearTimeout(this.keyboardCloseTimeout);
      this.keyboardCloseTimeout = null;
    }
  }

  /** Checks if the given element is an input or textarea */
  private isInputElement(element: HTMLElement): boolean {
    return element != null && this.VIEWPORT_CONFIG.INPUT_ELEMENT_REGEX.test(element.tagName);
  }
}
